import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Ingredient } from 'Types/api';
import { isScalableAmount, parseAmount } from 'Utils/amountUtils';

interface ScaledIngredientsListProps {
  ingredients: Ingredient[];
  originalIngredients: Ingredient[];
  onIngredientAmountChange: (index: number, newAmount: number) => void;
  isScaled: boolean;
}

const ScaledIngredientsList: React.FC<ScaledIngredientsListProps> = ({
  ingredients,
  originalIngredients,
  onIngredientAmountChange,
  isScaled,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleStartEdit = (index: number, currentAmount: string) => {
    if (!isScalableAmount(currentAmount)) return;
    setEditingIndex(index);
    setEditValue(currentAmount);
  };

  const handleConfirmEdit = () => {
    if (editingIndex === null) return;

    const newAmount = parseAmount(editValue);
    if (newAmount !== null && newAmount > 0) {
      onIngredientAmountChange(editingIndex, newAmount);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const isAmountChanged = (index: number): boolean => {
    return isScaled && ingredients[index]?.amount !== originalIngredients[index]?.amount;
  };

  return (
    <List dense>
      {ingredients.map((ingredient, index) => {
        const scalable = isScalableAmount(originalIngredients[index]?.amount || '');
        const changed = isAmountChanged(index);

        return (
          <ListItem
            key={index}
            disablePadding
            sx={{
              py: 0.5,
              '&:hover .edit-button': {
                opacity: 1,
              },
            }}
          >
            {editingIndex === index ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {ingredient.name}
                </Typography>
                <TextField
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  size='small'
                  autoFocus
                  sx={{ width: '80px' }}
                  inputProps={{ 'aria-label': 'New amount' }}
                />
                <Typography variant='body2' color='text.secondary'>
                  {ingredient.unit || ''}
                </Typography>
                <IconButton size='small' onClick={handleConfirmEdit} color='primary'>
                  <CheckIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={handleCancelEdit}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ListItemText
                  primary={
                    <Box
                      component='span'
                      sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}
                    >
                      <Typography component='span' variant='body1' sx={{ fontWeight: 600 }}>
                        {ingredient.name}
                      </Typography>
                      {ingredient.amount === 'to taste' ? (
                        <Typography
                          component='span'
                          variant='body2'
                          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                        >
                          to taste
                        </Typography>
                      ) : (
                        <Typography
                          component='span'
                          variant='body2'
                          sx={{
                            color: changed ? 'primary.main' : 'text.secondary',
                            fontWeight: changed ? 500 : 400,
                          }}
                        >
                          {ingredient.amount}
                          {ingredient.unit ? ` ${ingredient.unit}` : ''}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {scalable && (
                  <Tooltip title='Scale by this ingredient'>
                    <IconButton
                      className='edit-button'
                      size='small'
                      onClick={() => handleStartEdit(index, ingredient.amount)}
                      sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                      aria-label={`Edit ${ingredient.name} amount`}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

export default ScaledIngredientsList;
